import React, { useEffect, useState } from 'react';
import { Spinner } from '@components/common/Spinner';
import { buildingInfoService } from '@services/building-info.service';
import { RegblData } from '../../types/index';

interface RegblTabProps {
  egid: number;
}

const fmt = (v?: number | null, decimals = 0): string =>
  v != null
    ? new Intl.NumberFormat('fr-CH', { maximumFractionDigits: decimals }).format(v)
    : '—';

const fmtDate = (iso?: string | null): string =>
  iso ? new Date(iso).toLocaleDateString('fr-CH') : '—';

const Row: React.FC<{ label: string; value?: string | number | null }> = ({ label, value }) => (
  <tr className="border-b border-gray-100 dark:border-gray-700 last:border-0">
    <td className="py-2.5 pr-4 text-sm text-gray-500 dark:text-gray-400 font-medium w-1/2">
      {label}
    </td>
    <td className="py-2.5 text-sm text-gray-800 dark:text-gray-200">
      {value ?? '—'}
    </td>
  </tr>
);

export const RegblTab: React.FC<RegblTabProps> = ({ egid }) => {
  const [data, setData] = useState<RegblData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const result = await buildingInfoService.fetchRegblData(egid);
        setData(result);
      } catch (err: any) {
        if (err?.response?.status === 404) {
          setError(`Aucune donnée RegBL disponible pour l'EGID ${egid}`);
        } else {
          setError('Impossible de charger les données RegBL (API GeoAdmin indisponible)');
        }
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [egid]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner size="lg" />
        <span className="ml-3 text-sm text-gray-500 dark:text-gray-400">
          Chargement des données RegBL...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-6 text-center">
        <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium rounded-full">
          EGID {egid}
        </span>
        <span className="text-xs text-gray-400 dark:text-gray-500">
          Source : Registre fédéral des bâtiments (GWR/RegBL) — api3.geo.admin.ch
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Identification et localisation */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
            Identification et localisation
          </h3>
          <table className="w-full">
            <tbody>
              <Row label="Coord. E (LV95)" value={data.coordE != null ? fmt(data.coordE) : undefined} />
              <Row label="Coord. N (LV95)" value={data.coordN != null ? fmt(data.coordN) : undefined} />
              <Row label="N° de parcelle" value={data.plotNumber} />
              <Row label="Catégorie de bâtiment" value={data.buildingCategory} />
              <Row label="Classe de bâtiment" value={data.buildingClass} />
              <Row label="Période de construction" value={data.constructionPeriod} />
            </tbody>
          </table>
        </div>

        {/* Caractéristiques physiques */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
            Caractéristiques physiques
          </h3>
          <table className="w-full">
            <tbody>
              <Row label="Nbre de niveaux" value={data.numberOfFloors} />
              <Row
                label="Logements dans le bâtiment"
                value={data.unitsInBuilding != null ? data.unitsInBuilding : undefined}
              />
              <Row
                label="Surface du bâtiment"
                value={data.buildingArea != null ? `${fmt(data.buildingArea)} m²` : undefined}
              />
              <Row
                label="Surface de réf. énergétique (SRE)"
                value={data.energyReferenceArea != null ? `${fmt(data.energyReferenceArea)} m²` : undefined}
              />
              <Row
                label="Volume du bâtiment"
                value={data.buildingVolume != null ? `${fmt(data.buildingVolume)} m³` : undefined}
              />
            </tbody>
          </table>
        </div>

        {/* Système de chauffage */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
            Système de chauffage principal
          </h3>
          <table className="w-full">
            <tbody>
              <Row label="Générateur de chaleur" value={data.heatingGenerator} />
              <Row label="Source d'énergie" value={data.heatingEnergy} />
              <Row label="Date de mise à jour" value={fmtDate(data.heatingDate)} />
            </tbody>
          </table>
        </div>

        {/* Système eau chaude */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
            Système eau chaude principal
          </h3>
          <table className="w-full">
            <tbody>
              <Row label="Générateur de chaleur" value={data.hotWaterGenerator} />
              <Row label="Source d'énergie" value={data.hotWaterEnergy} />
              <Row label="Date de mise à jour" value={fmtDate(data.hotWaterDate)} />
            </tbody>
          </table>
        </div>

        {/* Données environnementales */}
        {(data.floodHazard != null || data.roadNoiseDb != null || data.railNoiseDb != null) && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 md:col-span-2">
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
              Données environnementales — GeoAdmin
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {data.floodHazard != null && (
                <div className={`rounded-lg p-3 border ${
                  data.floodHazard === 'high'   ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' :
                  data.floodHazard === 'medium' ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800' :
                  data.floodHazard === 'low'    ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800' :
                  'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                }`}>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Risque inondation</p>
                  <p className={`text-sm font-semibold ${
                    data.floodHazard === 'high'   ? 'text-red-700 dark:text-red-300' :
                    data.floodHazard === 'medium' ? 'text-orange-700 dark:text-orange-300' :
                    data.floodHazard === 'low'    ? 'text-yellow-700 dark:text-yellow-300' :
                    'text-green-700 dark:text-green-300'
                  }`}>
                    {data.floodHazard === 'high' ? 'Élevé (rouge)' :
                     data.floodHazard === 'medium' ? 'Modéré (bleu)' :
                     data.floodHazard === 'low' ? 'Faible (jaune)' : 'Aucun'}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">ch.bafu.hydrologische-gefahren</p>
                </div>
              )}
              {data.roadNoiseDb != null && (
                <div className={`rounded-lg p-3 border ${
                  data.roadNoiseDb >= 65 ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' :
                  data.roadNoiseDb >= 60 ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800' :
                  'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                }`}>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Bruit routier (nuit)</p>
                  <p className={`text-sm font-semibold ${
                    data.roadNoiseDb >= 65 ? 'text-red-700 dark:text-red-300' :
                    data.roadNoiseDb >= 60 ? 'text-orange-700 dark:text-orange-300' :
                    'text-green-700 dark:text-green-300'
                  }`}>
                    {fmt(data.roadNoiseDb, 1)} dB(A)
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">ch.bafu.laermbelastung-strassen_nacht</p>
                </div>
              )}
              {data.railNoiseDb != null && (
                <div className={`rounded-lg p-3 border ${
                  data.railNoiseDb >= 65 ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' :
                  data.railNoiseDb >= 60 ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800' :
                  'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                }`}>
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Bruit ferroviaire (nuit)</p>
                  <p className={`text-sm font-semibold ${
                    data.railNoiseDb >= 65 ? 'text-red-700 dark:text-red-300' :
                    data.railNoiseDb >= 60 ? 'text-orange-700 dark:text-orange-300' :
                    'text-green-700 dark:text-green-300'
                  }`}>
                    {fmt(data.railNoiseDb, 1)} dB(A)
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">ch.bafu.laermbelastung-eisenbahnen_nacht</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
